"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import withBrand from "@/components/with-brand";
import { SingleTrip } from "@/config/app-constant";
import { usePromoBanner } from "@/hooks/use-promo-banner";
import { RootState } from "@/store/root-reducer";

import ApplyButton from "./apply-button";
import TermsLink from "./terms-link";

interface PromoCodeBannerProps {
  // Single Trip (ST) - Without Title Content
  showBanner?: boolean;
  discount?: string;
  description?: string;
  promoCode?: string;
  showTerms?: boolean;
  termsText?: string;
  termsUrl?: string;
  showButton?: boolean;
  applyButtonLabel?: string;
  appliedButtonLabel?: string;
  // Multi Trip (AMT) - Without Title Content
  mtWithoutTitleShowBanner?: boolean;
  mtWithoutTitleDiscount?: string;
  mtWithoutTitleDescription?: string;
  mtWithoutTitlePromoCode?: string;
  mtWithoutTitleShowTerms?: boolean;
  mtWithoutTitleTermsText?: string;
  mtWithoutTitleTermsUrl?: string;
  mtWithoutTitleShowButton?: boolean;
  mtWithoutTitleApplyButtonLabel?: string;
  mtWithoutTitleAppliedButtonLabel?: string;
  // Configuration
  bannerStatus?: "disabled" | "experiment" | "enabled";
  className?: string;
  brand?: string;
}

// Brand configuration helper function
const getBrandConfig = (isAvanti: boolean) => ({
  bgColor: isAvanti ? "#FFD873" : undefined,
  bgGradientDesktop: isAvanti
    ? undefined
    : "linear-gradient(90.32deg, #880F27 2.69%, #8F3F96 57.47%)",
  bgGradientMobile: isAvanti
    ? undefined
    : "linear-gradient(130.38deg, #880F27 3.29%, #522367 73.34%)",
  textColor: isAvanti ? "#005A5E" : undefined,
  promoCodeColor: isAvanti ? "#E2005D" : "#F6B052",
  buttonColor: isAvanti ? "#005A5E" : "#E8F7E9",
  iconStroke: isAvanti ? "#005A5E" : "white",
  fontWeights: {
    main: isAvanti ? "font-[700]" : "font-[500]",
    discount: isAvanti ? "font-[700]" : "font-[600]",
    useCode: isAvanti ? "font-[700]" : "font-[600]",
    button: isAvanti ? "font-[700]" : "font-[600]",
    terms: isAvanti ? "font-[600]" : "font-[400]",
    mobileGet: isAvanti ? "font-[600]" : "font-[500]",
    mobileDescription: isAvanti ? "font-[600]" : "font-[400]",
  },
  lineHeight: isAvanti ? "leading-[24px]" : "leading-[22px]",
  buttonUnderline: isAvanti
    ? "underline decoration-2 underline-offset-[6px]"
    : "underline",
  termsUnderline: isAvanti
    ? "underline decoration-2 underline-offset-[4px]"
    : "underline",
  linkPosition: isAvanti ? "relative -top-[1px]" : "",
});

function PromoCodeBanner(props: Readonly<PromoCodeBannerProps>) {
  const {
    bannerStatus = "disabled",
    className = "",
    brand = "staysure",
  } = props;

  // Get trip type from Redux to determine which content to show
  const tripDetails = useSelector(
    (state: RootState) => state.quote.attributes.tripDetails
  );
  const isSingleTrip = tripDetails?.policyType === SingleTrip;

  // Extract appropriate content based on trip type
  const discount = isSingleTrip
    ? props.discount || "20% off"
    : props.mtWithoutTitleDiscount || "20% off";

  const description = isSingleTrip
    ? props.description || "your travel insurance in our Summer Sale."
    : props.mtWithoutTitleDescription || "your travel insurance in our Summer Sale.";

  const promoCode = isSingleTrip
    ? props.promoCode || "SEP20"
    : props.mtWithoutTitlePromoCode || "SEP20";

  const termsText = isSingleTrip
    ? props.termsText || "T&Cs apply."
    : props.mtWithoutTitleTermsText || "T&Cs apply.";

  const termsUrl = isSingleTrip
    ? props.termsUrl || "#"
    : props.mtWithoutTitleTermsUrl || "#";

  const applyButtonLabel = isSingleTrip
    ? props.applyButtonLabel || "Apply code"
    : props.mtWithoutTitleApplyButtonLabel || "Apply code";

  const appliedButtonLabel = isSingleTrip
    ? props.appliedButtonLabel || "Applied"
    : props.mtWithoutTitleAppliedButtonLabel || "Applied";

  // Visibility controls - default to false if not specified
  const showBanner = isSingleTrip
    ? props.showBanner ?? false
    : props.mtWithoutTitleShowBanner ?? false;

  const showButton = isSingleTrip
    ? props.showButton ?? false
    : props.mtWithoutTitleShowButton ?? false;

  const showTerms = isSingleTrip
    ? props.showTerms ?? false
    : props.mtWithoutTitleShowTerms ?? false;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] =
    useState<React.JSX.Element | null>(null);
  const previousPromoCodeRef = React.useRef<string | null>(null);
  const hasInitialized = React.useRef(false);

  const { isPromoBannerActive, applyPromoCode, isButtonDisabled, newQuote } =
    usePromoBanner(promoCode, bannerStatus);

  const appliedPromoCode = useSelector(
    (state: RootState) =>
      state.quote.attributes?.paymentDetails?.discounts?.promoCode
  );

  // Capture the initial promo code from Redux when component mounts (only once)
  useEffect(() => {
    if (
      !hasInitialized.current &&
      appliedPromoCode?.valid &&
      appliedPromoCode?.code
    ) {
      previousPromoCodeRef.current = appliedPromoCode.code.toLowerCase();
      hasInitialized.current = true;
    }
  }, [appliedPromoCode]);

  // Check for promo code validation errors and success after quote update
  useEffect(() => {
    if (newQuote) {
      const appliedCode =
        newQuote?.attributes?.paymentDetails?.discounts?.promoCode;

      if (appliedCode) {
        if (!appliedCode.valid && appliedCode.message) {
          setErrorMessage(
            "The promo code couldn't be applied at the moment. Please try again, or come back in a moment."
          );
          setSuccessMessage(null);
        } else if (appliedCode.valid && appliedCode.code) {
          setErrorMessage(null);

          // Check if this is a code switch (previous code exists and is different from current)
          const currentCode = appliedCode.code.toLowerCase();
          const previousCode = previousPromoCodeRef.current;

          if (previousCode && previousCode !== currentCode) {
            setSuccessMessage(
              <>
                Promo code <span className="font-bold">{appliedCode.code}</span>{" "}
                has been applied. Previous promo code removed.
              </>
            );
          } else {
            setSuccessMessage(null);
          }
        }
      }
    }
  }, [newQuote]);

  // Check if this banner's promo code is already applied
  const isPromoAlreadyApplied = useMemo(() => {
    return (
      appliedPromoCode?.valid &&
      appliedPromoCode?.code?.toLowerCase() === promoCode?.toLowerCase()
    );
  }, [appliedPromoCode, promoCode]);

  // Wrapper function to apply promo code
  const handleApplyPromoCode = async () => {
    await applyPromoCode();
  };

  if (!isPromoBannerActive) return null;
  if (!showBanner) return null;

  // Brand-specific configuration
  const isAvanti = brand === "avanti";
  const brandConfig = getBrandConfig(isAvanti);

  // Button text position only for Avanti when promo is NOT applied
  const buttonTextPosition =
    isAvanti && !isPromoAlreadyApplied ? "relative -top-[3px]" : "";

  return (
    <div
      className={`col-span-12 mx-spacing-l md:mx-spacing-xxl mt-spacing-m lg:my-spacing-xl lg:mb-spacing-xs`}
    >
      {/* Desktop View (xl and up) */}
      <header
        className={`hidden xl:flex relative rounded-[16px] py-spacing-s text-white shadow-md mx-auto ${className}`}
        style={{
          background: brandConfig.bgColor || brandConfig.bgGradientDesktop,
          maxWidth: "1104px",
        }}
        aria-label="Promotional offer"
      >
        <div className="flex flex-col w-full gap-spacing-xxs">
          {/* Content wrapper with flex-wrap for responsive layout */}
          <div className="flex items-center justify-center flex-wrap gap-x-spacing-m gap-y-spacing-xs w-full px-spacing-xl">
            {/* Tag Icon and Get text grouped */}
            <div className="flex items-center gap-[12px]">
              {/* Tag Icon */}
              <div className="flex-shrink-0">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M10.75 10.75H10.76M5.75 5.75H5.76M10.75 0.75H0.75V10.75L10.04 20.04C10.98 20.98 12.52 20.98 13.46 20.04L20.04 13.46C20.98 12.52 20.98 10.98 20.04 10.04L10.75 0.75Z"
                    stroke={brandConfig.iconStroke}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Get 20% off your travel insurance text */}
              <span
                className={`${brandConfig.fontWeights.main} text-[20px] leading-[23px]`}
                style={
                  brandConfig.textColor
                    ? { color: brandConfig.textColor }
                    : undefined
                }
              >
                <span className="whitespace-nowrap">
                  Get{" "}
                  <span
                    className={`${brandConfig.fontWeights.discount} text-[20px] leading-[23px]`}
                  >
                    {discount}
                  </span>
                </span>
                {description}
              </span>
            </div>

            {/* Use code */}
            <span
              className={`${brandConfig.fontWeights.useCode} text-[20px] leading-[27px] whitespace-nowrap`}
              style={{ color: brandConfig.promoCodeColor }}
            >
              Use code: {promoCode}
            </span>
            {showButton && (
              <ApplyButton
                textSize="text-[18px]"
                iconSize={isPromoAlreadyApplied ? 24 : 19.5}
                className="flex-shrink-0"
                isPromoAlreadyApplied={isPromoAlreadyApplied}
                applyButtonLabel={applyButtonLabel}
                appliedButtonLabel={appliedButtonLabel}
                isButtonDisabled={isButtonDisabled}
                promoCode={promoCode}
                onApplyPromoCode={handleApplyPromoCode}
                style={{ color: brandConfig.buttonColor }}
                fontWeight={brandConfig.fontWeights.button}
                underlineClass={brandConfig.buttonUnderline}
                positionClass={buttonTextPosition}
              />
            )}
            {showTerms && termsText && termsUrl && (
              <TermsLink
                termsText={termsText}
                termsUrl={termsUrl}
                className="whitespace-nowrap"
                style={
                  brandConfig.textColor
                    ? { color: brandConfig.promoCodeColor }
                    : undefined
                }
                fontWeight={brandConfig.fontWeights.terms}
                underlineClass={brandConfig.termsUnderline}
                positionClass={brandConfig.linkPosition}
              />
            )}
          </div>
        </div>
      </header>

      {/* Mobile/Tablet View */}
      <header
        className={`xl:hidden relative rounded-[16px] px-spacing-m py-spacing-s text-white shadow-md ${className}`}
        style={{
          background: brandConfig.bgColor || brandConfig.bgGradientMobile,
        }}
        aria-label="Promotional offer"
      >
        {/* Content stacked vertically */}
        <div className="flex flex-col gap-[4px]">
          {/* First line - discount, description and promo code all in one line */}
          <div
            className={`text-[16px] lg:text-[20px] ${brandConfig.lineHeight} text-center`}
          >
            <span
              className={brandConfig.fontWeights.mobileGet}
              style={
                brandConfig.textColor
                  ? { color: brandConfig.textColor }
                  : undefined
              }
            >
              Get{" "}
              <span className={brandConfig.fontWeights.discount}>
                {discount}
              </span>
            </span>{" "}
            <span
              className={brandConfig.fontWeights.mobileDescription}
              style={
                brandConfig.textColor
                  ? { color: brandConfig.textColor }
                  : undefined
              }
            >
              {description}
            </span>{" "}
            <span
              className="font-[600] whitespace-nowrap"
              style={{ color: brandConfig.promoCodeColor }}
            >
              {isAvanti ? (
                <>
                  <span className="md:hidden">Code</span>
                  <span className="hidden md:inline">Use code</span>
                </>
              ) : (
                "Use code"
              )}
              : {promoCode}
            </span>
          </div>

          {/* Bottom row - T&Cs and Apply button - center aligned */}
          <div className="flex items-center justify-center gap-spacing-s mt-[2px]">
            {showButton && (
              <ApplyButton
                textSize="text-[14px] lg:text-[18px]"
                iconSize={isPromoAlreadyApplied ? 24 : 15.75}
                isPromoAlreadyApplied={isPromoAlreadyApplied}
                applyButtonLabel={applyButtonLabel}
                appliedButtonLabel={appliedButtonLabel}
                isButtonDisabled={isButtonDisabled}
                promoCode={promoCode}
                onApplyPromoCode={handleApplyPromoCode}
                style={{ color: brandConfig.buttonColor }}
                fontWeight={brandConfig.fontWeights.button}
                underlineClass={brandConfig.buttonUnderline}
                positionClass={buttonTextPosition}
              />
            )}
            {showTerms && termsText && termsUrl && (
              <TermsLink
                termsText={termsText}
                termsUrl={termsUrl}
                style={{ color: brandConfig.promoCodeColor }}
                fontWeight={brandConfig.fontWeights.terms}
                underlineClass={brandConfig.termsUnderline}
                positionClass={brandConfig.linkPosition}
              />
            )}
          </div>
        </div>
      </header>

      {/* Error Message - Below banner for both views */}
      {errorMessage && (
        <div
          className="text-center text-[16px] leading-[24px] font-[400] font-body-font text-text-critical mt-[10px]"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {/* Success Message - Below banner for both views */}
      {successMessage && (
        <div
          className="text-center text-[16px] leading-[24px] font-[400] font-body-font text-text-success mt-[10px] text-text-positive"
          role="alert"
        >
          {successMessage}
        </div>
      )}
    </div>
  );
}

export default withBrand(PromoCodeBanner);
